/**
 * GlobalTableExplorerDialog
 *
 * A globally-rendered Table Explorer dialog that can be triggered from anywhere
 * in the app via the tableExplorerDialogStore.
 */

import { useCallback } from 'react';
import { TableExplorerDialog } from './TableExplorerDialog';
import { useTableExplorerDialogStore } from '../store/tableExplorerDialogStore';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { useConnectionStore } from '../../../connections/store/connectionStore';
import { getSelectedTableInfo } from '../../../database-objects/utils/getSelectedTableInfo';
import { openDataProfileTab } from '../../data-profile/services/dataProfileNavigation';
import { openMigrateTableTab } from '../../../cross-engine-migrate/openMigrateTab';
import type { TableInfo } from '../hooks/useAvailableTables';
import type { ExplorerSelectedTable } from './TableSelector';
import { safeConnectionServer } from '../../../../types/safeConnectionDisplay';

export function GlobalTableExplorerDialog() {
  const {
    isOpen,
    connectionId,
    connectionName,
    serverHost,
    database,
    engineId,
    purpose,
    closeDialog,
  } = useTableExplorerDialogStore();

  const { addTab } = useWorkspaceStore();
  const { activeConnections } = useConnectionStore();

  // Get explorer selected table for quick access
  const explorerSelectedTable = useCallback((): ExplorerSelectedTable | undefined => {
    const selectedInfo = getSelectedTableInfo();
    if (!selectedInfo) return undefined;
    return {
      name: selectedInfo.name,
      schema: selectedInfo.schema,
      database: selectedInfo.database,
    };
  }, [])();

  const handleSelectTable = useCallback(
    (table: TableInfo) => {
      if (purpose === 'open-profile') {
        openDataProfileTab({
          connectionId,
          database: table.database,
          schema: table.schema ?? '',
          table: table.name,
          entryPoint: 'toolbar',
        });
        closeDialog();
        return;
      }

      // Find the active connection to get server info
      const activeConnection = activeConnections.find((c) => c.id === connectionId);

      if (purpose === 'migrate-table') {
        openMigrateTableTab({
          connectionId,
          connectionName,
          serverHost: serverHost || safeConnectionServer(activeConnection?.connection),
          database: table.database,
          engine: activeConnection?.connection.engine ?? engineId,
          schema: table.schema ?? '',
          table: table.name,
        });
        closeDialog();
        return;
      }

      addTab({
        title: table.schema ? `${table.schema}.${table.name}` : table.name,
        type: 'table-explorer',
        connectionInfo: {
          connectionId,
          connectionName,
          serverHost: serverHost || safeConnectionServer(activeConnection?.connection),
          database: table.database,
        },
        content: {
          tableName: table.name,
          schema: table.schema,
          database: table.database,
          rows: [],
          columns: [],
          limit: 100,
          selectedColumns: '*',
          filters: [],
          sorting: [],
          isLoading: true,
        },
      });

      closeDialog();
    },
    [purpose, connectionId, connectionName, serverHost, engineId, activeConnections, addTab, closeDialog]
  );

  if (!isOpen) return null;

  return (
    <TableExplorerDialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) closeDialog();
      }}
      connectionId={connectionId}
      database={database}
      engineId={engineId}
      explorerSelectedTable={explorerSelectedTable}
      onSelectTable={handleSelectTable}
      titleVariant={purpose === 'open-profile' ? 'profile' : 'explorer'}
    />
  );
}
